import { CategoryNode, CategoryTree } from '../types/category';
import { amountsFromTotal, amountsForNoVat } from './amountUtils';

export const NO_VAT_DISPLAY = 'N/A';

const normName = (name?: string | null): string => (name || '').trim().toLowerCase();

const findNamedChild = (
  nodes: CategoryNode[] | undefined,
  name?: string | null
): CategoryNode | undefined => {
  const key = normName(name);
  if (!key || !nodes) return undefined;
  return nodes.find((node) => normName(node.name) === key);
};

export function isNoVatCategory(
  tree: CategoryTree | null | undefined,
  category1?: string | null,
  category2?: string | null,
  category3?: string | null
): boolean {
  const node1 = findNamedChild(tree?.categories, category1);
  if (node1?.no_vat) return true;
  if (!normName(category2)) return false;
  const node2 = findNamedChild(node1?.children, category2);
  if (node2?.no_vat) return true;
  if (!normName(category3)) return false;
  const node3 = findNamedChild(node2?.children, category3);
  return Boolean(node3?.no_vat);
}

type AmountFields = {
  amount: number;
  vatAmount?: number;
  totalAmount?: number;
  noVat?: boolean;
  category1: string;
  category2: string;
  category3: string;
};

export function withCategoryAmountSync<T extends AmountFields>(
  prev: T,
  nextCategories: { category1: string; category2: string; category3: string },
  tree: CategoryTree | null | undefined
): T {
  const noVat = isNoVatCategory(
    tree,
    nextCategories.category1,
    nextCategories.category2,
    nextCategories.category3
  );
  if (noVat) {
    const next = amountsForNoVat(prev.amount, prev.vatAmount, prev.totalAmount);
    return { ...prev, ...nextCategories, ...next, noVat: true };
  }
  if (prev.noVat) {
    const total = prev.totalAmount || prev.amount;
    if (Number.isFinite(total) && total !== 0) {
      const next = amountsFromTotal(total);
      return { ...prev, ...nextCategories, ...next, noVat: false };
    }
    return { ...prev, ...nextCategories, vatAmount: 0, noVat: false };
  }
  return { ...prev, ...nextCategories, noVat: false };
}
